import "@patternfly/react-core/dist/styles/base-no-reset.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Flex,
  FlexItem,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadMain,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Page
} from "@patternfly/react-core";
import { ChartLineIcon, HomeIcon, LayerGroupIcon, QuestionCircleIcon, ShieldAltIcon, SignOutAltIcon, ToolsIcon } from "@patternfly/react-icons";
import type {
  DashboardPayload,
  Location,
  MePayload,
  NavigationItem,
  NavigationPayload,
  User,
  ViewKey
} from "./types";
import { canAddWarehouseInventory } from "./display";
import { apiGet } from "./app/api";
import { LegacySync } from "./app/LegacySync";
import { navigateWithFallback, signOutWithFallback } from "./app/legacyBridge";
import { APP_VERSION, fallbackNavigation, iconForView, navLabel, visibleTabs } from "./app/navigation";
import { OrderDetailDialog } from "./components/OrderDetailDialog";
import { AddInventoryWorkspace } from "./screens/addInventory/AddInventoryWorkspace";
import { AuditTrailWorkspace } from "./screens/auditTrail/AuditTrailWorkspace";
import { BranchRequestsWorkspace } from "./screens/branchRequests/BranchRequestsWorkspace";
import { DispatchControlWorkspace } from "./screens/dispatchControl/DispatchControlWorkspace";
import { FinanceCloseWorkspace } from "./screens/financeClose/FinanceCloseWorkspace";
import { InventoryActionsWorkspace } from "./screens/inventoryActions/InventoryActionsWorkspace";
import { PosSalesWorkspace } from "./screens/posSales/PosSalesWorkspace";
import { ReportsWorkspace } from "./screens/reports/ReportsWorkspace";
import { SetupWorkspace } from "./screens/setup/SetupWorkspace";
import { StoreCountWorkspace } from "./screens/storeCount/StoreCountWorkspace";
import { SyncStatusWorkspace } from "./screens/syncStatus/SyncStatusWorkspace";
import { TodayWorkspace } from "./screens/today/TodayWorkspace";
import { UserAccessWorkspace } from "./screens/userAccess/UserAccessWorkspace";
import { VendorPurchasingWorkspace } from "./screens/vendorPurchasing/VendorPurchasingWorkspace";
import { WasteLossWorkspace } from "./screens/wasteLoss/WasteLossWorkspace";
import { PosMappingWorkspace } from "./screens/posMapping/PosMappingWorkspace";
import "./patternfly-toj.css";

type SessionStatusPayload = {
  authenticated?: boolean;
  app_version?: string;
  ok?: boolean;
};

const PATTERNFLY_ACTIVE_VIEWS = new Set<ViewKey>([
  "dashboard",
  "po_request",
  "add_inventory",
  "vendor_po",
  "production",
  "finance_close",
  "sales",
  "audit",
  "sync_status",
  "report_daily_inventory",
  "report_all_branches_inventory",
  "report_product_sales_count",
  "admin",
  "db_edits",
  "user_access",
  "store_inventory",
  "store_count",
  "manual_adjustments"
]);

function resetPageScroll() {
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.querySelectorAll<HTMLElement>(".pf-v6-c-page, .pf-v6-c-page__main").forEach(element => {
      element.scrollTop = 0;
      element.scrollLeft = 0;
    });
  });
}

function PatternFlyShell() {
  const [user, setUser] = useState<User | null>(() => window.tojIms?.getState().user || null);
  const [view, setView] = useState<ViewKey>(() => window.tojIms?.getState().view || "dashboard");
  const [locations, setLocations] = useState<Location[]>(() => window.tojIms?.getState().locations || []);
  const [csrfToken, setCsrfToken] = useState("");
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [error, setError] = useState("");
  const [openMenu, setOpenMenu] = useState<"inventory" | "finance" | "reports" | "setup" | null>(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [serverNavigation, setServerNavigation] = useState<NavigationItem[]>([]);
  const [orderDialogId, setOrderDialogId] = useState<string | number | null>(null);

  const syncFromLegacy = useCallback(() => {
    const legacy = window.tojIms?.getState();
    setUser(legacy?.user || null);
    setView(legacy?.view || "dashboard");
    setLocations(legacy?.locations || []);
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!window.tojIms?.getState().user) return;
    setLoadingDashboard(true);
    setError("");
    try {
      const payload = await apiGet<DashboardPayload>("/api/dashboard");
      setDashboard(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Today could not be loaded.");
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  const loadNavigation = useCallback(async () => {
    if (!window.tojIms?.getState().user) return;
    try {
      const payload = await apiGet<NavigationPayload>("/api/navigation");
      setServerNavigation(payload.menu.map(item => ({ ...item, icon: iconForView(item.key) })));
    } catch {
      setServerNavigation([]);
    }
  }, []);

  const sessionIsActive = useCallback(async () => {
    try {
      const payload = await apiGet<SessionStatusPayload>("/api/session-status");
      return Boolean(payload.authenticated);
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    document.body.classList.add("pf-milestone-one");
    return () => document.body.classList.remove("pf-milestone-one");
  }, []);

  useEffect(() => {
    syncFromLegacy();
    let cancelled = false;
    void sessionIsActive().then(active => {
      if (!active || cancelled) {
        if (!cancelled) setUser(null);
        return;
      }
      void apiGet<MePayload>("/api/me")
        .then(payload => {
          if (cancelled) return;
          setUser(payload.user);
          setCsrfToken(payload.csrf_token || "");
        })
        .catch(() => {
          if (!cancelled) setUser(null);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [sessionIsActive, syncFromLegacy]);

  useEffect(() => {
    if (user) return undefined;
    let cancelled = false;
    const syncIfSignedIn = async () => {
      if (cancelled) return;
      const legacy = window.tojIms?.getState();
      if (legacy?.user) {
        syncFromLegacy();
        return;
      }
      try {
        if (!(await sessionIsActive())) return;
        const payload = await apiGet<MePayload>("/api/me");
        if (!cancelled && payload.user) {
          setUser(payload.user);
          setCsrfToken(payload.csrf_token || "");
        }
      } catch {
        // A signed-out shell is normal on the login screen.
      }
    };
    const timer = window.setInterval(() => {
      void syncIfSignedIn();
    }, 750);
    void syncIfSignedIn();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [sessionIsActive, syncFromLegacy, user]);

  useEffect(() => {
    if (user && view === "dashboard") void loadDashboard();
  }, [loadDashboard, user, view]);

  useEffect(() => {
    if (user) {
      void loadNavigation();
    } else {
      setServerNavigation([]);
      setCsrfToken("");
    }
  }, [loadNavigation, user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void apiGet<MePayload>("/api/me")
      .then(payload => {
        if (cancelled) return;
        if (payload.user) setUser(payload.user);
        setCsrfToken(payload.csrf_token || "");
      })
      .catch(() => {
        if (!cancelled) setCsrfToken("");
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const allowedTabs = useMemo(() => visibleTabs(user), [user]);
  const navigationItems = useMemo(() => serverNavigation.length ? serverNavigation : fallbackNavigation, [serverNavigation]);
  const groupedNav = useMemo(() => {
    const visibleMenuItems = (group: "inventory" | "finance" | "reports" | "setup") => (
      navigationItems.filter(item => item.group === group && item.active_menu !== false && allowedTabs.has(item.key))
    );
    return {
      inventory: visibleMenuItems("inventory"),
      finance: visibleMenuItems("finance"),
      reports: visibleMenuItems("reports"),
      setup: visibleMenuItems("setup")
    };
  }, [allowedTabs, navigationItems]);

  const navigate = useCallback(async (nextView: ViewKey) => {
    setOpenMenu(null);
    await navigateWithFallback(nextView);
    syncFromLegacy();
    resetPageScroll();
    void loadNavigation();
  }, [loadNavigation, syncFromLegacy]);

  const openOrder = useCallback(async (id: string | number) => {
    setOrderDialogId(id);
  }, []);

  const orderChanged = useCallback(() => {
    window.dispatchEvent(new Event("toj:orders-changed"));
    void loadDashboard();
    void loadNavigation();
  }, [loadDashboard, loadNavigation]);

  const orders = useMemo(() => {
    if (dashboard?.dashboard_type === "in_house") return dashboard.in_house_requests || [];
    return dashboard?.purchase_orders || [];
  }, [dashboard]);

  const actionRows = useMemo(() => orders.filter(row => row.next_action_is_mine !== false).slice(0, 8), [orders]);

  if (!user) return null;

  const updateSelectedLocation = (value: string) => {
    setSelectedLocation(value);
    const legacyFilter = document.querySelector<HTMLSelectElement>("#locationFilter");
    if (legacyFilter) {
      legacyFilter.value = value;
      legacyFilter.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  const dropdown = (
    key: "inventory" | "finance" | "reports" | "setup",
    label: string,
    icon: React.ReactNode,
    items: NavigationItem[]
  ) => {
    if (!items.length) return null;
    return (
      <Dropdown
        key={key}
        isOpen={openMenu === key}
        onOpenChange={isOpen => setOpenMenu(isOpen ? key : null)}
        toggle={toggleRef => (
          <MenuToggle
            ref={toggleRef}
            isExpanded={openMenu === key}
            onClick={() => setOpenMenu(openMenu === key ? null : key)}
            icon={icon}
            className={`pf-toj-nav-tab ${items.some(item => item.key === view) ? "pf-toj-nav-active" : ""}`}
          >
            {label}
          </MenuToggle>
        )}
      >
        <DropdownList>
          {items.map(item => (
            <DropdownItem
              key={item.key}
              icon={item.icon}
              onClick={() => void navigate(item.key)}
            >
              {navLabel(item, user)}
            </DropdownItem>
          ))}
        </DropdownList>
      </Dropdown>
    );
  };

  const masthead = (
        <Masthead className="pf-toj-masthead" role="banner">
          <MastheadMain>
            <MastheadBrand>
              <img className="pf-toj-brand-logo" src="/logo-text.png" alt="Taste of Joy" />
              <div className="pf-toj-brand-copy">
                <span>{user.full_name} | {user.role}</span>
              </div>
            </MastheadBrand>
          </MastheadMain>
          <MastheadContent>
            <div className="pf-toj-masthead-content-grid">
              <Flex alignItems={{ default: "alignItemsCenter" }} spaceItems={{ default: "spaceItemsSm" }} className="pf-toj-nav">
                <FlexItem>
                  <Button
                    variant="plain"
                    icon={<HomeIcon />}
                    onClick={() => void navigate("dashboard")}
                    className={`pf-toj-nav-tab ${view === "dashboard" ? "pf-toj-nav-current" : ""}`}
                  >
                    Today
                  </Button>
                </FlexItem>
                <FlexItem>{dropdown("inventory", "Inventory Controls", <LayerGroupIcon />, groupedNav.inventory)}</FlexItem>
                <FlexItem>{dropdown("finance", "Finance", <ShieldAltIcon />, groupedNav.finance)}</FlexItem>
                <FlexItem>{dropdown("reports", "Reports", <ChartLineIcon />, groupedNav.reports)}</FlexItem>
                <FlexItem>
                  {allowedTabs.has("user_access") && (
                    <Button
                      variant="plain"
                      icon={<ShieldAltIcon />}
                      onClick={() => void navigate("user_access")}
                      className={`pf-toj-nav-tab ${view === "user_access" ? "pf-toj-nav-current" : ""}`}
                    >
                      User Access
                    </Button>
                  )}
                </FlexItem>
                <FlexItem>{dropdown("setup", "Setup", <ToolsIcon />, groupedNav.setup)}</FlexItem>
              </Flex>
              <Flex alignItems={{ default: "alignItemsCenter" }} spaceItems={{ default: "spaceItemsSm" }} className="pf-toj-session-actions">
                <FlexItem>
                  <Button variant="secondary" icon={<QuestionCircleIcon />} onClick={() => setHelpOpen(true)}>Help</Button>
                </FlexItem>
                <FlexItem>
                  <Button variant="secondary" icon={<SignOutAltIcon />} onClick={() => void signOutWithFallback()}>Sign out</Button>
                </FlexItem>
              </Flex>
            </div>
          </MastheadContent>
        </Masthead>
  );

  const patternflyActive = PATTERNFLY_ACTIVE_VIEWS.has(view);

  return (
    <>
      <LegacySync onSync={syncFromLegacy} />
      {patternflyActive ? (
      <Page masthead={masthead}>
        {view === "dashboard" && (
          <TodayWorkspace
            user={user}
            dashboard={dashboard}
            loading={loadingDashboard}
            error={error}
            locations={locations}
            selectedLocation={selectedLocation}
            orders={orders}
            actionRows={actionRows}
            onNavigate={navigate}
            onSelectedLocationChange={updateSelectedLocation}
            onOpenOrder={openOrder}
          />
        )}
        {view === "po_request" && allowedTabs.has("po_request") && (
          <BranchRequestsWorkspace user={user} csrfToken={csrfToken} onOpenOrder={openOrder} />
        )}
        {view === "add_inventory" && canAddWarehouseInventory(user) && (
          <AddInventoryWorkspace csrfToken={csrfToken} />
        )}
        {view === "vendor_po" && allowedTabs.has("vendor_po") && (
          <VendorPurchasingWorkspace csrfToken={csrfToken} />
        )}
        {view === "production" && allowedTabs.has("production") && (
          <DispatchControlWorkspace user={user} onOpenOrder={openOrder} />
        )}
        {view === "finance_close" && allowedTabs.has("finance_close") && (
          <FinanceCloseWorkspace csrfToken={csrfToken} />
        )}
        {view === "sales" && allowedTabs.has("sales") && (
          <PosSalesWorkspace csrfToken={csrfToken} />
        )}
        {view === "audit" && allowedTabs.has("audit") && (
          <AuditTrailWorkspace />
        )}
        {view === "sync_status" && allowedTabs.has("sync_status") && (
          <SyncStatusWorkspace csrfToken={csrfToken} />
        )}
        {(view === "report_daily_inventory" || view === "report_all_branches_inventory" || view === "report_product_sales_count") && allowedTabs.has(view) && (
          <ReportsWorkspace view={view} csrfToken={csrfToken} />
        )}
        {view === "admin" && allowedTabs.has("admin") && (
          <SetupWorkspace csrfToken={csrfToken} />
        )}
        {view === "db_edits" && allowedTabs.has("db_edits") && (
          <PosMappingWorkspace csrfToken={csrfToken} />
        )}
        {view === "user_access" && allowedTabs.has("user_access") && (
          <UserAccessWorkspace csrfToken={csrfToken} />
        )}
        {view === "store_inventory" && (
          <InventoryActionsWorkspace user={user} />
        )}
        {view === "store_count" && allowedTabs.has("store_count") && (
          <StoreCountWorkspace csrfToken={csrfToken} />
        )}
        {view === "manual_adjustments" && allowedTabs.has("manual_adjustments") && (
          <WasteLossWorkspace csrfToken={csrfToken} />
        )}
      </Page>
      ) : (
        <div className="pf-toj-legacy-shell">{masthead}</div>
      )}
      <Modal
        variant={ModalVariant.small}
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      >
        <ModalHeader title="Help" />
        <ModalBody>
          <div className="pf-toj-help-body">
            <strong>What do you need to do?</strong>
            <p>Use the top menu to open inventory work, branch requests, vendor purchasing, reports, user access, or setup.</p>
            <p>Items that need attention are shown on Today. Open one row to continue that work.</p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setHelpOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <OrderDetailDialog
        orderId={orderDialogId}
        user={user}
        csrfToken={csrfToken}
        onClose={() => setOrderDialogId(null)}
        onChanged={orderChanged}
      />
    </>
  );
}

function mountPatternFlyShell() {
  const root = document.getElementById("patternfly-root");
  if (!root) return;
  createRoot(root).render(<PatternFlyShell />);
  document.documentElement.dataset.patternflyVersion = APP_VERSION;
}

mountPatternFlyShell();






